import React, { useState, useCallback, Fragment, useRef } from 'react';
import { useDropzone } from 'react-dropzone';

import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import uploadFile from '../util/upload';
import { isRequirementGroupSatisfied } from '../util/safetyCertificates';
import { getAcceptedExtensions } from '../util/acceptedFormats.js';
import { DocumentCamera } from './DocumentCamera.jsx';

const clampPercent = (value) => {
	if (!Number.isFinite(value)) return 0;
	return Math.min(100, Math.max(0, value));
};

const toDisplayPercent = (value) => {
	// Clamp and always round up to an integer.
	return Math.ceil(clampPercent(value));
};

const MultipleFileUpload = ({
	onFilesUploaded,
	uploadUrl,
	disabled,
	text,
	existingFiles = [],
	accept = {
		'application/pdf': ['.pdf'],
		'image/png': ['.png'],
		'image/jpeg': ['.jpeg', '.jpg'],
	},
	fileType = 'generic',
	context = {},
	allowedDocumentTypes = [],
	possibleDocumentTypes = [],
	requiredDocumentTypes = [],
	getSatisfiedTypes = null,
	showAiProcessingState = false,
	documentTypesMap = {},
	useCamera = true,
	cameraIncludes = '',
	cameraRotationAngle = 0,
}) => {
	const [uploadingFiles, setUploadingFiles] = useState({});
	const [uploadProgress, setUploadProgress] = useState({});
	const [failedFiles, setFailedFiles] = useState({});
	const [showCamera, setShowCamera] = useState(false);

	// useCamera only takes effect when a device header is present (deviceId= in the URL);
	// on regular (non-kiosk) sessions it has no impact.
	const effectiveUseCamera = useCamera && window.location.href.includes('deviceId=');

	// Update ref when existingFiles changes
	const currentFilesRef = useRef(existingFiles);
	currentFilesRef.current = existingFiles;

	const onDrop = useCallback(async (acceptedFiles) => {
		const fileUploadPromises = [];

		for (const file of acceptedFiles) {
			const fileId = `${file.name}-${Date.now()}-${Math.random()}`;

			// Add placeholder for the file being uploaded immediately
			setUploadingFiles(prev => ({ ...prev, [fileId]: true }));
			setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

			// Create upload promise
			const uploadPromise = (async () => {
				try {
					const arrayBuffer = await file.arrayBuffer();
					const uploadedFile = await uploadFile(uploadUrl, arrayBuffer, false, {
						progressHandler: (progress) =>
							setUploadProgress(prev => ({ ...prev, [fileId]: clampPercent(progress) })),
						contentType: file.type,
						filename: file.name,
						type: fileType,
						context,
						allowedDocumentTypes,
					});

					// Remove from uploading state
					setUploadingFiles(prev => {
						const updated = { ...prev };
						delete updated[fileId];
						return updated;
					});
					setUploadProgress(prev => {
						const updated = { ...prev };
						delete updated[fileId];
						return updated;
					});

					// Add to successful files immediately
					const successfulFile = {
						...uploadedFile,
						fileName: file.name,
						processingFeedback: uploadedFile.metadata?.validityFeedback || 'Document uploaded successfully',
						validity: uploadedFile.metadata?.validity !== undefined ? uploadedFile.metadata.validity : true,
					};

					// Update the files list immediately when this file completes
					const updatedFiles = [...currentFilesRef.current, successfulFile];
					currentFilesRef.current = updatedFiles;
					onFilesUploaded(updatedFiles);

				} catch (error) {
					console.error('Upload error for file:', file.name, error);

					// Determine error message based on error type
					let errorMessage = 'Upload failed';
					if (error.message?.includes('401')) {
						errorMessage = 'Unauthorized - please check your permissions';
					} else if (error.message?.includes('403')) {
						errorMessage = 'Forbidden - access denied';
					} else if (error.message?.includes('404')) {
						errorMessage = 'Upload endpoint not found';
					} else if (error.message?.includes('500')) {
						errorMessage = 'Server error - please try again';
					} else if (error.message?.includes('Network')) {
						errorMessage = 'Network error - please check your connection';
					} else if (error.message) {
						errorMessage = error.message;
					}

					// Remove from uploading state and add to failed files
					setUploadingFiles(prev => {
						const updated = { ...prev };
						delete updated[fileId];
						return updated;
					});
					setUploadProgress(prev => {
						const updated = { ...prev };
						delete updated[fileId];
						return updated;
					});

					setFailedFiles(prev => ({ ...prev, [fileId]: errorMessage }));
				}
			})();

			fileUploadPromises.push(uploadPromise);
		}

		// Wait for all uploads to complete (but files are already updated individually)
		await Promise.all(fileUploadPromises);
	}, [existingFiles, uploadUrl, onFilesUploaded, fileType]);

	const handleCameraCapture = useCallback(async (e) => {
		const blob = e.target.files[0];
		const file = new File([blob], `camera-${Date.now()}.png`, { type: blob.type || 'image/png' });
		await onDrop([file]);
		setShowCamera(false);
	}, [onDrop]);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		disabled: disabled || Object.keys(uploadingFiles).length > 0,
		accept,
		multiple: true,
	});

	const acceptedExtensions = getAcceptedExtensions(accept);

	const removeFile = (index) => {
		// Work only with files from this zone
		const newFiles = existingFiles.filter((_, i) => i !== index);
		onFilesUploaded(newFiles); // Only files from this zone
	};

	const dismissFailedUpload = (fileId) => {
		setFailedFiles(prev => {
			const updated = { ...prev };
			delete updated[fileId];
			return updated;
		});
	};

	// Helper to check if a file satisfies any required types
	const satisfiesRequiredType = (file) => {
		if (!file.type || file.validity === false) return false;

		// If getSatisfiedTypes function is provided, use it with nested array support
		if (getSatisfiedTypes) {
			const satisfiedTypes = getSatisfiedTypes(file.type);
			return requiredDocumentTypes.some(requirementGroup =>
				isRequirementGroupSatisfied(requirementGroup, satisfiedTypes),
			);
		}

		// Fallback to simple match (backwards compatible with flat arrays)
		return requiredDocumentTypes.some(requirementGroup => {
			if (Array.isArray(requirementGroup)) {
				// Nested array - check if file type matches any in the group
				return requirementGroup.includes(file.type);
			} else {
				// String - direct match
				return requirementGroup === file.type;
			}
		});
	};

	// Helper to check if a file satisfies any possible types
	const satisfiesPossibleType = (file) => {
		if (!file.type || file.validity === false) return false;

		// If getSatisfiedTypes function is provided, use it with nested array support
		if (getSatisfiedTypes) {
			const satisfiedTypes = getSatisfiedTypes(file.type);
			return possibleDocumentTypes.some(requirementGroup =>
				isRequirementGroupSatisfied(requirementGroup, satisfiedTypes),
			);
		}

		// Fallback to simple match (backwards compatible with flat arrays)
		return possibleDocumentTypes.some(requirementGroup => {
			if (Array.isArray(requirementGroup)) {
				// Nested array - check if file type matches any in the group
				return requirementGroup.includes(file.type);
			} else {
				// String - direct match
				return requirementGroup === file.type;
			}
		});
	};

	const categorizedFiles = {
		required: existingFiles.filter(file => satisfiesRequiredType(file)),
		optional: existingFiles.filter(file => !satisfiesRequiredType(file) && satisfiesPossibleType(file)),
		other: existingFiles.filter(file => !satisfiesRequiredType(file) && !satisfiesPossibleType(file)),
	};

	const getDocumentTypeDisplayName = (typeId) =>
		(documentTypesMap[typeId]?.name ?? typeId);

	const renderFileCard = (file, index) => (
		<Card key={index} variant="outlined" sx={{
			marginBottom: 2,
			borderColor: file.validity === false ? 'error.main' : undefined,
		}}>
			
			<ListItem sx={{ alignItems: 'center' }}>
				<ListItemIcon>
					<CheckIcon color={file.validity === false ? 'error' : 'success'} />
				</ListItemIcon>
				<ListItemText
					sx={{
						flex: 1,
						minWidth: 0,
						marginRight: 1,
					}}
					primary={file.fileName || file.name}
					primaryTypographyProps={{
						sx: {
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						},
					}}
					secondary={
						<Fragment>
							{file.type != null && file.type !== '' && (
								<Typography variant='body2' color='text.secondary' sx={{ wordBreak: 'break-word' }}>
									{(text.recognizedAs ?? 'Recognized as')}: {getDocumentTypeDisplayName(file.type)}
								</Typography>
							)}
							{file.processingFeedback && (
								<Typography
									variant='body2'
									color={file.validity === false ? 'error' : 'text.secondary'}
									sx={{ wordBreak: 'break-word' }}>

									{text.processingFeedback}: {file.processingFeedback}
								</Typography>
							)}
						</Fragment>
					}
				/>
				<Button
					startIcon={<DeleteIcon />}
					onClick={() => removeFile(existingFiles.indexOf(file))}
					disabled={disabled}
					color="error"
					size="small"
					sx={{ flexShrink: 0 }}
				>
					{text.removeDocument}
				</Button>
			</ListItem>
		</Card>
	);

	return (
		<Box>
			<Fragment>
				{/* Required Documents */}
				{categorizedFiles.required.length > 0 && (
					<Box sx={{ marginBottom: 3 }}>
						<Typography variant="subtitle2" sx={{ marginBottom: 1, fontWeight: 'bold' }}>
							{text.requiredDocumentsUploaded || 'Required Documents'}
						</Typography>
						{categorizedFiles.required.map((file, index) => renderFileCard(file, index))}
					</Box>
				)}

				{/* Optional Documents */}
				{categorizedFiles.optional.length > 0 && (
					<Box sx={{ marginBottom: 3 }}>
						<Typography variant="subtitle2" sx={{ marginBottom: 1, fontWeight: 'bold' }}>
							{text.optionalDocumentsUploaded || 'Optional Documents'}
						</Typography>
						{categorizedFiles.optional.map((file, index) => renderFileCard(file, index))}
					</Box>
				)}

				{/* Other Documents */}
				{categorizedFiles.other.length > 0 && (
					<Box sx={{ marginBottom: 3 }}>
						<Typography variant="subtitle2" sx={{ marginBottom: 1, fontWeight: 'bold' }}>
							{text.otherDocumentsUploaded || 'Other Documents'}
						</Typography>
						{categorizedFiles.other.map((file, index) => renderFileCard(file, index))}
					</Box>
				)}
			</Fragment>

			{/* Uploading Files */}
			{Object.entries(uploadingFiles).map(([fileId]) => {
				const fileName = fileId.split('-')[0];
				const progress = uploadProgress[fileId] || 0;
				const percent = toDisplayPercent(progress);
				const isAiProcessing = showAiProcessingState && percent === 100;
				return (
					<Card key={fileId} variant="outlined" sx={{ marginBottom: 2 }}>
						<ListItem>
							<ListItemIcon>
								{isAiProcessing ? (
									<AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: 28 }} />
								) : (
									<CircularProgress size={24} variant="determinate" value={percent} />
								)}
							</ListItemIcon>
							<ListItemText
								primary={text.uploading + ` ${fileName}...`}
								secondary={
									isAiProcessing
										? (text.aiProcessingSubtext || 'AI is processing your document')
										: `${percent}%`
								}
							/>
						</ListItem>
					</Card>
				);
			})}

			{/* Failed Files */}
			{Object.entries(failedFiles).map(([fileId, errorMessage]) => {
				const fileName = fileId.split('-')[0];
				return (
					<Card key={fileId} variant="outlined" sx={{ marginBottom: 2, borderColor: 'error.main' }}>
						<ListItem>
							<ListItemIcon>
								<ErrorIcon color="error" />
							</ListItemIcon>
							<ListItemText
								primary={text.uploadFailed + `: ${fileName}`}
								secondary={
									<Fragment>
										<Typography variant='body2' color="error">
											{errorMessage}
										</Typography>
									</Fragment>
								}
							/>
							<Button
								onClick={() => dismissFailedUpload(fileId)}
								disabled={disabled}
								color="error"
								size="small"
							>
								Dismiss
							</Button>
						</ListItem>
					</Card>
				);
			})}

			{/* Dropzone or Camera */}
			{effectiveUseCamera ? (
				<>
					<Card
						variant="outlined"
						sx={{
							border: '2px dashed #ccc',
							backgroundColor: '#fafafa',
							cursor: disabled ? 'not-allowed' : 'pointer',
							'&:hover': disabled ? {} : { backgroundColor: '#f0f0f0' },
						}}
						onClick={() => !disabled && setShowCamera(true)}
					>
						<ListItem sx={{ height: 100 }}>
							<ListItemIcon>
								<UploadIcon />
							</ListItemIcon>
							<ListItemText
								primary={text.uploadDocument}
							/>
						</ListItem>
					</Card>
					{showCamera && (
						<DocumentCamera
							cameraIncludes={cameraIncludes}
							cameraRotationAngle={cameraRotationAngle}
							onClose={() => setShowCamera(false)}
							onImage={handleCameraCapture}
						/>
					)}
				</>
			) : (
				<Card
					variant="outlined"
					sx={{
						border: '2px dashed #ccc',
						backgroundColor: isDragActive ? '#f0f0f0' : '#fafafa',
						cursor: disabled ? 'not-allowed' : 'pointer',
						'&:hover': disabled ? {} : { backgroundColor: '#f0f0f0' },
					}}
					{...getRootProps()}
				>
					<input {...getInputProps()} />
					<ListItem sx={{ height: 100 }}>
						<ListItemIcon>
							<UploadIcon />
						</ListItemIcon>
						<ListItemText
							primary={isDragActive ? text.dropFileHere : text.uploadDocument}
							secondary={`${text.dragDropFile} ${acceptedExtensions.length > 0 ? `(${acceptedExtensions.join(', ')})` : ''}`}
						/>
					</ListItem>
				</Card>
			)}
		</Box>
	);
};

MultipleFileUpload.type = 'MultipleFileUpload';
MultipleFileUpload.spec = [{
	name: 'MultipleFileUpload',
	key: 'MultipleFileUpload',
	title: 'Multiple file upload with drag and drop',
	allowedFields: [],
	fields: [
		{
			type: 'TextField',
			name: 'label',
			label: 'Label',
		},
		{
			type: 'TextField',
			name: 'uploadUrl',
			label: 'Upload URL',
		},
		{
			type: 'TextField',
			name: 'fileType',
			label: 'File type',
		},
	],
	defaultProperties: {
		uploadUrl: '/api/files',
		fileType: 'generic',
	},
}];

export default MultipleFileUpload;
